import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconMapPin, IconChevronDown } from '@tabler/icons-react';
import { Loader2 } from 'lucide-react';
import { fetchPortalCustomerAccessSites } from '../services/boardChatService';
import './BoardChatAccessSites.css';

export function BoardChatAccessSites({ conversationId }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [state, setState] = useState({ key: null, loading: false, error: '', contratos: [] });

  useEffect(() => {
    setOpen(false);
    setState({ key: null, loading: false, error: '', contratos: [] });
  }, [conversationId]);

  const load = useCallback(() => {
    if (!conversationId) return;
    setState({ key: conversationId, loading: true, error: '', contratos: [] });
    fetchPortalCustomerAccessSites(conversationId)
      .then((res) => {
        setState({
          key: conversationId,
          loading: false,
          error: '',
          contratos: Array.isArray(res?.data?.contratos) ? res.data.contratos : [],
        });
      })
      .catch((e) => {
        setState({
          key: conversationId,
          loading: false,
          error: e?.message || 'Não foi possível carregar os locais.',
          contratos: [],
        });
      });
  }, [conversationId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && (state.key !== conversationId || state.error)) load();
  };

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setCoords(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const width = Math.min(340, window.innerWidth - 24);
      setCoords({
        left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
        top: rect.bottom + 6,
        width,
      });
    };
    update();
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDoc = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.bch-access-pop')) return;
      setOpen(false);
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc, true);
    };
  }, [open]);

  const totalSites = state.contratos.reduce((n, c) => n + (c.locais?.length || 0), 0);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`bch-access-btn${open ? ' is-open' : ''}`}
        onClick={toggle}
        aria-expanded={open}
      >
        <IconMapPin size={13} stroke={1.8} />
        <span>Locais de acesso</span>
        <IconChevronDown size={12} stroke={2} className="bch-access-btn-chev" />
      </button>
      {open && coords
        ? createPortal(
            <div
              className="bch-access-pop"
              style={{ left: coords.left, top: coords.top, width: coords.width }}
              role="dialog"
              aria-label="Locais de acesso"
            >
              <div className="bch-access-pop-head">
                <span>Locais de acesso</span>
                {!state.loading && !state.error && totalSites ? (
                  <span className="bch-access-pop-count">
                    {totalSites} {totalSites === 1 ? 'site' : 'sites'}
                  </span>
                ) : null}
              </div>
              <div className="bch-access-pop-body">
                {state.loading ? (
                  <div className="bch-access-pop-empty">
                    <Loader2 size={14} className="bch-access-spin" />
                    Carregando…
                  </div>
                ) : state.error ? (
                  <div className="bch-access-pop-empty bch-access-pop-empty--err">{state.error}</div>
                ) : !state.contratos.length ? (
                  <div className="bch-access-pop-empty">Nenhum local vinculado no cadastro.</div>
                ) : (
                  state.contratos.map((c) => (
                    <div className="bch-access-group" key={c.contrato}>
                      <div className="bch-access-group-head">
                        <span className="bch-access-group-code">{c.contrato}</span>
                        {c.classificacao ? (
                          <span className="bch-access-group-name">{c.classificacao}</span>
                        ) : null}
                        <span className="bch-access-group-count">{c.locais.length}</span>
                      </div>
                      {c.locais.length ? (
                        <ul className="bch-access-list">
                          {c.locais.map((l) => (
                            <li key={`${c.contrato}|${l.codigo}`}>
                              <span className="bch-access-site">{l.nomeSite || `Site ${l.codigo}`}</span>
                              {l.endereco ? <span className="bch-access-addr">{l.endereco}</span> : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="bch-access-pop-empty">Sites não encontrados no ERP.</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
